import { isNil } from 'lodash-es';
import { type PropsWithChildren } from 'react';

import { SparklineContainer } from './SparklineContainer.js';
import type { SparklineDisplayProps } from './types/sparkline-display-props.js';
import { getWrapperStyle } from './utils/get-wrapper-style.js';
import { useBehavioralTrackingProps } from '../../../../core/hooks/useBehavioralTrackingProps.js';
import { defaultChartSize } from '../../../../core/styles/defaultChartValues.sty.js';
import type { Timeseries } from '../../types/timeseries.js';
import { ChartStateHandler } from '../chart-states/ChartStateHandler.js';
import { getChartStatesTemplates } from '../chart-states/utils/get-chart-states-template.js';

const COMPONENT_NAME = 'Sparkline';

/**
 * SparklineDisplay component
 */
export const SparklineDisplay = (
  props: PropsWithChildren<SparklineDisplayProps>,
) => {
  const {
    data,
    loading = false,
    children,
    width,
    height,
    style,
    className,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    'data-testid': dataTestId,
  } = props;

  const [behavioralTrackingProps] = useBehavioralTrackingProps(props);

  const series: Timeseries = Array.isArray(data) ? data[0] : data;
  const { emptyState, errorState } = getChartStatesTemplates(children);

  const isEmpty = isNil(series) || series.datapoints?.length === 0;

  const wrapperStyle = getWrapperStyle(width, height, style);

  return (
    <ChartStateHandler
      {...behavioralTrackingProps}
      data-testid={dataTestId ?? 'sparkline-state-handler'}
      empty={isEmpty}
      height="100%"
      emptyState={emptyState}
      errorState={errorState}
      isLoading={loading}
      className={className ?? defaultChartSize}
      style={wrapperStyle}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      data-dt-component={COMPONENT_NAME}
    >
      <SparklineContainer {...props} data={series} />
    </ChartStateHandler>
  );
};

SparklineDisplay['displayName'] = 'SparklineDisplay';
